/**
 * Visit-time helpers — Eqaya anchors all site/assessment visit times to the
 * PROPERTY's timezone, not the viewer's. Every property is in Zimbabwe
 * (Central Africa Time, UTC+2, no daylight saving), so a site visit is shown
 * and entered in Harare time regardless of whether the viewer is a diaspora
 * client in the US/UK or a contractor on the ground. This removes the
 * "is that 9am my time or their time?" confusion: both parties see the same
 * labelled number, and it matches when the contractor physically shows up.
 *
 * Zimbabwe has never observed DST, so the offset is a constant +02:00 — we can
 * convert datetime-local input strings with a fixed offset, and format instants
 * with the IANA zone for correctness/robustness.
 */
export const PROPERTY_TZ = 'Africa/Harare';
export const PROPERTY_TZ_LABEL = '(Zimbabwe Time)';
const CAT_OFFSET = '+02:00';

// en-GB renders 12-hour time as lowercase "3:00 pm" — owners asked for "3:00 PM".
const upperMeridiem = (s) => s.replace(/\b(am|pm)\b/g, (m) => m.toUpperCase());

/**
 * Format a UTC/ISO instant as Harare wall-clock time with a "CAT" label.
 * opts: { withDate=true, withYear=true, weekday=false, dateOnly=false }
 */
export const formatHarare = (value, opts = {}) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const { withDate = true, withYear = true, weekday = false, dateOnly = false } = opts;
  const fmt = { timeZone: PROPERTY_TZ };
  if (weekday) fmt.weekday = 'short';
  if (withDate) { fmt.day = 'numeric'; fmt.month = 'short'; if (withYear) fmt.year = 'numeric'; }
  if (!dateOnly) { fmt.hour = 'numeric'; fmt.minute = '2-digit'; fmt.hour12 = true; }
  const label = dateOnly ? '' : ` ${PROPERTY_TZ_LABEL}`;
  return `${upperMeridiem(d.toLocaleString('en-GB', fmt))}${label}`;
};

/**
 * "22 Sep 2026" in Harare time — the date a thing happened where the property
 * is, not where the viewer is sitting. A diaspora buyer in New York and the
 * agent in Borrowdale must read the same verified date off the same listing.
 *
 * The month comes from a table rather than month:'short' because en-GB renders
 * September as "Sept" under Node/ICU, which reads oddly beside the other eleven
 * three-letter months. Asking Intl for a numeric month keeps the Harare
 * timezone conversion while leaving the wording to us.
 *
 * Returns null (not '') for a null or unparseable value, so callers can choose
 * their own fallback wording.
 */
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const formatHarareDateShort = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const p = new Intl.DateTimeFormat('en-GB', {
    timeZone: PROPERTY_TZ, day: 'numeric', month: 'numeric', year: 'numeric',
  }).formatToParts(d).reduce((a, x) => { a[x.type] = x.value; return a; }, {});
  const month = MONTHS_SHORT[Number(p.month) - 1];
  if (!month) return null;
  return `${Number(p.day)} ${month} ${p.year}`;
};

/** A visit entry is either an exact ISO time or a { start, end } window. */
export const windowStart = (w) => (typeof w === 'object' && w !== null && w.start ? w.start : w);

/**
 * Format a visit entry (exact time or { start, end } window) in Harare time,
 * e.g. "Fri, 10 Jul 2026, 09:00 – 12:00 CAT".
 */
export const formatHarareWindow = (w) => {
  if (!w) return '';
  const startIso = windowStart(w);
  const start = new Date(startIso);
  if (Number.isNaN(start.getTime())) return '';
  const startLabel = upperMeridiem(start.toLocaleString('en-GB', {
    timeZone: PROPERTY_TZ, weekday: 'short', day: 'numeric', month: 'short',
    year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true
  }));
  if (typeof w === 'object' && w !== null && w.end) {
    const end = new Date(w.end);
    if (!Number.isNaN(end.getTime())) {
      // same-day windows only need the end time; otherwise repeat the day
      const startDay = start.toLocaleDateString('en-CA', { timeZone: PROPERTY_TZ });
      const endDay = end.toLocaleDateString('en-CA', { timeZone: PROPERTY_TZ });
      const endLabel = upperMeridiem(end.toLocaleString('en-GB', startDay === endDay
        ? { timeZone: PROPERTY_TZ, hour: 'numeric', minute: '2-digit', hour12: true }
        : { timeZone: PROPERTY_TZ, weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true }));
      return `${startLabel} – ${endLabel} ${PROPERTY_TZ_LABEL}`;
    }
  }
  return `${startLabel} ${PROPERTY_TZ_LABEL}`;
};

/**
 * A datetime-local <input> yields a zoneless wall-clock string
 * ("2026-07-10T09:00"). Interpret it as HARARE time and return a UTC ISO
 * string. Without this the browser would read the picker in the viewer's own
 * timezone, so a US client's "9am" would be stored as their 9am, not the
 * property's.
 */
export const harareInputToIso = (localStr) => {
  if (!localStr) return null;
  const s = localStr.length === 16 ? `${localStr}:00` : localStr; // add seconds if absent
  const d = new Date(`${s}${CAT_OFFSET}`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

/**
 * Inverse of harareInputToIso: render a UTC/ISO instant as the Harare
 * wall-clock string a datetime-local input expects ("YYYY-MM-DDTHH:mm").
 */
export const isoToHarareInput = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone: PROPERTY_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false
  }).formatToParts(d).reduce((a, x) => { a[x.type] = x.value; return a; }, {});
  const hour = p.hour === '24' ? '00' : p.hour; // some engines emit 24 for midnight
  return `${p.year}-${p.month}-${p.day}T${hour}:${p.minute}`;
};
